import { Translate } from "next-translate";
import * as zod from "zod";
import { TSelectOption } from "~/components/atoms/form/SelectField";
import { PrCategory } from "~/models/pr/category";
import { Project } from "~/models/project";

export const FIRESTORE_COLLECTION_NAME_TOPIC = "topics";

export interface Topic {
  articleCVAverageLast30Days?: number;
  articleFbHelpfulAverageLast30Days?: number;
  articlePVAverageLast30Days?: number;
  articleTextCountAverage?: number;
  callForNewArticleWriting: boolean;
  coverImageUrl?: string;
  createdUnixTime: number;
  description: string;
  id: string;
  mainImageUrl?: string;
  makerName?: string;
  name: string;
  nameEn?: string;
  prCategoryNames?: PrCategory["name"][];
  prDisplayType: string;
  projectId: Project["id"];
  prProductId?: number;
  prTagName?: string;
  qaCountWithLocales?: { [locale: string]: number };
  shortName?: string;
  slug: string;
  twitterTags: string[];
  updatedUnixTime: number;
}

export enum TopicImageTypeEnum {
  cover = "cover",
  icon = "icon",
}

export enum TopicPrDisplayTypeEnum {
  label = "label",
  none = "none",
  popular_and_cheap = "popular_and_cheap",
  product_group = "product_group",
  sell = "sell",
}

export interface TopicIDictionary {
  [index: string]: Topic;
}

export const TOPIC_PR_DISPLAY_TYPE_OPTIONS = [
  { text: "表示しない", value: "none" },
  { text: "Label 表示", value: "label" },
  { text: "Sell 表示", value: "sell" },
  { text: "商品グループ表示", value: "product_group" },
  { text: "人気商品＆最安値情報", value: "popular_and_cheap" },
];

export const TOPIC_SCHEMA = (t: Translate) =>
  zod
    .object({
      callForNewArticleWriting: zod.boolean(),
      description: zod
        .string()
        .nonempty({
          message: t("form:validation.empty-input", {
            name: t("topic:label-description"),
          }),
        })
        .min(80, {
          message: t("form:validation.min-max", { max: 400, min: 80 }),
        })
        .max(400, {
          message: t("form:validation.min-max", { max: 400, min: 80 }),
        }),
      makerName: zod.string(),
      name: zod.string().nonempty({
        message: t("form:validation.empty-input", {
          name: t("topic:label-topic-name"),
        }),
      }),
      prCategoryNames: zod.string(),
      prDisplayType: zod.string().nonempty({
        message: t("form:validation.empty-select", {
          name: "表示種別",
        }),
      }),
      prProductId: zod
        .string()
        .nullable()
        .refine((value) => prProductIdValidate(value), {
          message: t("form:validation.empty-input", {
            name: t("topic:label-pr-product-id"),
          }),
        }),
      prTagName: zod.string(),
      projectId: zod.string().nonempty({
        message: t("form:validation.empty-select", {
          name: t("common:label-project"),
        }),
      }),
      shortName: zod.string(),
      slug: zod
        .string()
        .nonempty({
          message: t("form:validation.empty-input", {
            name: t("topic:label-slug"),
          }),
        })
        .refine((value) => slugValidate(value), {
          message: t("form:validation.invalid-format"),
        }),
      twitterTags: zod.string(),
    })
    .refine(
      (data) =>
        data.prDisplayType === TopicPrDisplayTypeEnum.none ||
        data.prDisplayType === TopicPrDisplayTypeEnum.popular_and_cheap ||
        data.prCategoryNames,
      {
        message: t("form:validation.empty-select", {
          name: "カテゴリ",
        }),
        path: ["prCategoryNames"],
      }
    )
    .refine(
      (data) =>
        data.prDisplayType === TopicPrDisplayTypeEnum.none ||
        data.prDisplayType === TopicPrDisplayTypeEnum.product_group ||
        data.prDisplayType === TopicPrDisplayTypeEnum.popular_and_cheap ||
        data.prProductId,
      {
        message: t("form:validation.empty-input", {
          name: "Product ID",
        }),
        path: ["prProductId"],
      }
    )
    .refine(
      (data) =>
        data.prDisplayType !== TopicPrDisplayTypeEnum.product_group ||
        data.prTagName,
      {
        message: t("form:validation.empty-input", {
          name: "Tag Name",
        }),
        path: ["prTagName"],
      }
    );

export function generateTopicOptions({
  allOptionText,
  projectId,
  topics,
  sort = "qaCount",
}: {
  allOptionText?: string;
  projectId: Project["id"] | undefined;
  topics?: Topic[];
  sort?: "name" | "qaCount";
}): TSelectOption[] {
  let filteredTopics = projectId
    ? topics?.filter((t) => t.projectId === projectId)
    : topics;

  // order topic by name
  filteredTopics = filteredTopics?.sort(
    sort === "name" ? topicNameSort : topicDefaultSort
  );

  const options =
    filteredTopics?.map((t) => ({ text: t.name, value: t.id })) || [];
  // prepend 全て option
  if (allOptionText !== undefined) {
    options.unshift({ text: allOptionText, value: "" });
  }

  return options;
}

function prProductIdValidate(value: string | null): boolean {
  if (!value) {
    return true;
  }

  const numberVal = parseInt(value);
  if (isNaN(numberVal) || numberVal < 1) {
    return false;
  }

  if (numberVal.toString() !== value) {
    return false;
  }

  return true;
}

function slugValidate(value: string): boolean {
  if (!value) {
    return false;
  }

  return !!value.match(/^[0-9a-z_]+$/);
}

export function topicDefaultSort(a: Topic, b: Topic): number {
  return toTopicTotalQaCount(b) - toTopicTotalQaCount(a);
}

function topicNameSort(a: Topic, b: Topic): number {
  return a.slug > b.slug ? 1 : b.slug > a.slug ? -1 : 0;
}

export function toTopicIDictionary(
  topics: Topic[] | undefined
): TopicIDictionary {
  if (!topics || !topics?.length) {
    return {};
  }

  return topics?.reduce(
    (arr: TopicIDictionary, topic) => ((arr[topic.id] = topic), arr),
    {}
  );
}

export function toTopicTotalQaCount(topic: Topic | undefined): number {
  if (!topic?.qaCountWithLocales) {
    return 0;
  }

  return (
    Object.values(topic?.qaCountWithLocales).reduce(
      (prev, next) => prev + next
    ) || 0
  );
}

export function getTopicShortName(topic: Topic) {
  return topic.shortName || topic.name;
}
